/**
 * LEVERANDØRENE (persontypene §3.2, migrering 1096).
 *
 * Leverandøren er et firma på årgangen, og det blir ikke en kontakt. Påmeldingen til kontaktpersonen
 * bærer status, kilde, samtykke og svar, så Leverandører-fanen er Frivillige-fanen med `kind=supplier`
 * (`listVolunteersQuerySchema`). Her står det som er nytt: skjemaets type, firmaet og de ansatte.
 *
 * Fila importerer ingenting fra `volunteers.ts` eller `signup-forms.ts`, fordi begge importerer herfra.
 */
import { z } from 'zod';

/** Skjemaets type: en frivillig, eller et firma med ansatte. */
export const SIGNUP_FORM_KINDS = ['volunteer', 'organization'] as const;
export const signupFormKindSchema = z.enum(SIGNUP_FORM_KINDS).meta({ id: 'SignupFormKind' });
export type SignupFormKind = z.infer<typeof signupFormKindSchema>;

/** Hvilken liste en påmelding står i: Frivillige eller Leverandører. */
export const SIGNUP_KINDS = ['volunteer', 'supplier'] as const;
export const signupKindSchema = z.enum(SIGNUP_KINDS).meta({ id: 'SignupKind' });
export type SignupKind = z.infer<typeof signupKindSchema>;

/** Hvor mange ansatte ett firma kan melde inn på én gang. */
export const MAX_SUPPLIER_MEMBERS = 200;

/** Én ansatt slik skjemaet, panelet og den personlige siden sender hen. E-post og telefon er valgfrie. */
export const supplierMemberInputSchema = z
  .object({
    givenName: z.string().trim().min(1).max(60),
    familyName: z.string().trim().min(1).max(60),
    email: z.email().max(200).nullish(),
    phone: z.string().trim().max(40).nullish(),
  })
  .meta({ id: 'SupplierMemberInput' });
export type SupplierMemberInput = z.infer<typeof supplierMemberInputSchema>;

/** Firmadelen av en innsending på et skjema av typen organisasjon. */
export const supplierSignupPartSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    members: z.array(supplierMemberInputSchema).max(MAX_SUPPLIER_MEMBERS),
  })
  .meta({ id: 'SupplierSignupPart' });
export type SupplierSignupPart = z.infer<typeof supplierSignupPartSchema>;

/** Firmaet på en påmeldingsrad: nok til å tegne lista uten et ekstra kall. */
export const supplierRefSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    memberCount: z.number().int().nonnegative(),
  })
  .meta({ id: 'SupplierRef' });
export type SupplierRef = z.infer<typeof supplierRefSchema>;

export const supplierMemberDtoSchema = z
  .object({
    id: z.uuid(),
    contactId: z.uuid(),
    personId: z.uuid(),
    displayName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
  })
  .meta({ id: 'SupplierMember' });
export type SupplierMemberDto = z.infer<typeof supplierMemberDtoSchema>;

export const supplierDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid(),
    /** Kontaktpersonens påmelding. Status, svar og notater står der. */
    signupId: z.uuid(),
    name: z.string(),
    /** Firmaets egen frist, når den er satt. Overstyrer skjemaets. */
    memberDeadline: z.iso.datetime().nullable(),
    /** Fristen som gjelder: firmaets, ellers skjemaets. Null betyr ingen frist. */
    effectiveDeadline: z.iso.datetime().nullable(),
    members: z.array(supplierMemberDtoSchema),
  })
  .meta({ id: 'Supplier' });
export type SupplierDto = z.infer<typeof supplierDtoSchema>;

/** «Legg til manuelt» (§3.3): firmaet, kontaktpersonen og de ansatte, uten at noen finnes fra før. */
export const createSupplierRequestSchema = z
  .object({
    editionId: z.uuid(),
    name: z.string().trim().min(1).max(200),
    contact: z.object({
      givenName: z.string().trim().min(1).max(60),
      familyName: z.string().trim().min(1).max(60),
      /** Påkrevd: lenken til den personlige siden går hit. */
      email: z.email().max(200),
      phone: z.string().trim().max(40).nullish(),
    }),
    members: z.array(supplierMemberInputSchema).max(MAX_SUPPLIER_MEMBERS).default([]),
    memberDeadline: z.iso.datetime().nullish(),
    /** Send lenken til den personlige siden til kontaktpersonen med en gang (§3.3). */
    sendLink: z.boolean().optional(),
  })
  .meta({ id: 'CreateSupplierRequest' });
export type CreateSupplierRequest = z.infer<typeof createSupplierRequestSchema>;

export const updateSupplierRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    memberDeadline: z.iso.datetime().nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateSupplierRequest' });
export type UpdateSupplierRequest = z.infer<typeof updateSupplierRequestSchema>;

// ------------------------------------------------------- den personlige siden (1097, «Ansatte»)

export const supplierPageMemberSchema = z
  .object({
    id: z.uuid(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    displayName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
  })
  .meta({ id: 'SupplierPageMember' });
export type SupplierPageMember = z.infer<typeof supplierPageMemberSchema>;

/**
 * Seksjonen «Ansatte» på kontaktpersonens personlige side: firmaet, fristen som gjelder og de
 * ansatte. `editable` er usann etter fristen; da står lista bare til lesing.
 */
export const supplierPageSchema = z
  .object({
    name: z.string(),
    deadline: z.iso.datetime().nullable(),
    editable: z.boolean(),
    members: z.array(supplierPageMemberSchema),
  })
  .meta({ id: 'SupplierPage' });
export type SupplierPage = z.infer<typeof supplierPageSchema>;
